import 'reflect-metadata';

import { autoAnimatePlugin } from '@formkit/auto-animate/vue';
import { browserName } from '@helpers/getBrowserDetails';
import {
  setAxiosAuthHeader,
  setAxiosCallSessionHeader,
  setAxiosCaregiverHeader,
  setAxiosOfficeHeader,
} from '@lib/axios';
import {
  clearCallSession,
  endCallSession,
  getCallSessionToken,
  readCallSession,
  TAN_LOGIN_PATH,
} from '@lib/callSession';
import {
  adoptOfficeFromUrl,
  chooseOffice,
  getActiveCaregiverId,
  getActiveOfficeId,
  restartAfterOfficeRefusal,
} from '@lib/activeOffice';
import { installGlobalErrorHandlers, installWindowErrorHandlers } from '@lib/errorReporting';
import keycloakIns, { initializeKeycloak } from '@lib/keycloak';
import { mintMediaSession } from '@lib/mediaSession';
import { setupQueryFocusManager } from '@lib/queryFocusManager';
import {
  ensureFreshToken,
  installApiInterceptors,
  isLoggingOut,
  logout,
  startKeycloakTokenRefresh,
  stopKeycloakTokenRefresh,
} from '@composables/session';
import App from '@src/App.vue';
import { clickOutsideDirective } from '@src/directives/click-outside.directive';
import errorFallbackHtml from '@src/error-fallback.html?raw';
import '@src/fonts.css';
import '@src/index.css';
import { logger } from '@src/lib/logger';
import { listenForPwaInstallPrompt } from '@lib/pwa';
import router from '@src/router';
import { usePushNotificationStore } from '@src/stores/pushNotificationStore';
import { useAuthStore } from '@stores/authStore'; // Import store
import { VueQueryPlugin } from '@tanstack/vue-query';
import {
  authQueries,
  initializeApiClient,
  registrationIntentMutations,
  registrationIntentQueries,
} from '@therame/api-client';
import { AuthContext, DecodedToken, Role } from '@therame/shared'; // Import context type
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/de';
import FloatingVue from 'floating-vue';
import { GesturePlugin } from '@vueuse/gesture';

dayjs.extend(isoWeek);
dayjs.locale('de');
import 'floating-vue/dist/style.css';
import { createPinia } from 'pinia';
import { createApp } from 'vue'; // Import markRaw

logger.debug({ msg: 'mode', mode: import.meta.env.VITE_MODE });

// Installed before bootstrapApp() runs so that anything thrown during startup —
// a failed dynamic import, a rejected keycloak promise — is recorded rather than
// only surfacing as the static error-fallback page.
installWindowErrorHandlers();

/**
 * Build the human-readable detail shown under "Fehlerdetails" on the bootstrap
 * fallback page. Axios is not the only thing that can fail here (keycloak, a bad
 * import, a thrown TypeError, ...), so we enrich Axios errors with the request,
 * status and backend trace id, and fall back to the stack / String() otherwise.
 */
const formatBootstrapError = (error: unknown): string => {
  // Prefix every detail block with the local time the error was shown, so a
  // copied report carries when it happened regardless of the error type.
  const timestamp = `Zeitpunkt: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`;

  if (isAxiosError(error)) {
    const lines = [timestamp, `${error.name}: ${error.message}`];

    if (error.code) {
      lines.push(`Code: ${error.code}`);
    }
    if (error.config?.url) {
      const method = error.config.method?.toUpperCase() ?? 'GET';
      lines.push(`Request: ${method} ${error.config.baseURL ?? ''}${error.config.url}`);
    }
    if (error.response) {
      lines.push(`Status: ${error.response.status} ${error.response.statusText}`);

      // The backend sets x-trace-id on every response (and mirrors it in the
      // error body); surface it so support can correlate. Absent on pure network
      // errors, where no response ever came back.
      const traceId =
        error.response.headers?.['x-trace-id'] ??
        (error.response.data as { traceId?: string } | undefined)?.traceId;
      if (traceId) {
        lines.push(`Trace-ID: ${traceId}`);
      }
    }

    return lines.join('\n');
  }

  if (error instanceof Error) {
    return `${timestamp}\n${error.stack ?? `${error.name}: ${error.message}`}`;
  }

  return `${timestamp}\n${String(error)}`;
};

/**
 * Logs in as the Person of the tab's office. An Account without any office may
 * have no Person at all (#1832): `/users/me` then refuses plainly, and the app
 * offers „Praxis eröffnen“ instead of failing to start. What the Account
 * registered for decides where it goes from there (#1833); a prevention signup
 * gets its officeless Person here, without a page (#1836).
 */
const loginOrFindNoOffice = async (authStore: ReturnType<typeof useAuthStore>) => {
  try {
    await authStore.login();
  } catch (error) {
    const refused = isAxiosError(error) && error.response?.status === 403;
    if (!refused || authStore.accountOffices.length > 0) throw error;
    const intent = await registrationIntentQueries.get().catch((intentError) => {
      logger.warn({ msg: 'Reading the registration intent failed', error: intentError });
      return null;
    });
    if (intent?.role === Role.PREVENTION && (await enterPrevention())) {
      await authStore.login();
      return;
    }
    authStore.accountWithoutOffice = true;
    authStore.registrationRole = intent?.role ?? null;
    authStore.registrationLinkToken = intent?.registrationLinkToken ?? null;
  }
};

/** Whether the prevention command gave the Account its officeless Person. */
const enterPrevention = () =>
  registrationIntentMutations.enterPrevention().then(
    () => true,
    (error) => {
      logger.warn({ msg: 'Entering prevention failed', error });
      return false;
    },
  );

const bootstrapApp = async () => {
  try {
    // Register the axios response interceptor before anything issues a request.
    // Formerly a module-load side-effect in lib/axios.ts; moved out so the leaf
    // layer no longer reaches into stores (TM-9047).
    installApiInterceptors();

    // A TAN link starts its tab afresh; a call token stored in the tab makes it
    // a TAN session (#1856). Neither initialises keycloak-js, so a Keycloak
    // session and the other tabs stay exactly as they are.
    const opensTanLink = window.location.pathname === TAN_LOGIN_PATH;
    if (opensTanLink) clearCallSession();
    const callSession = readCallSession();
    const authenticated =
      opensTanLink || callSession ? false : await initializeKeycloak(keycloakIns);

    listenForPwaInstallPrompt();
    setupQueryFocusManager();

    const pinia = createPinia();

    // Initialize API client for @therame/api-client queries
    if (callSession) {
      initializeApiClient({
        baseURL: import.meta.env.VITE_API_ROOT as string,
        // The token is the only credential and has no refresh: a 401 means the
        // TAN is gone or over, and the tab starts over at the TAN page.
        onUnauthorized: endCallSession,
        onNetworkError: (error) => {
          logger.warn({ msg: 'API request failed without a response', error });
        },
        getCallSessionToken,
      });
    } else {
      initializeApiClient({
        baseURL: import.meta.env.VITE_API_ROOT as string,
        // Only escalate a 401 to an involuntary logout when we actually held a
        // session. `keycloak.init()` runs without `onLoad`, so on a plain reload
        // the app mounts unauthenticated for the moment it takes the router guard
        // to redirect to the login — any query firing in that window 401s
        // legitimately. Treating that as an expired session sends the user to
        // Keycloak's "Wollen Sie sich abmelden?" confirmation (there is no
        // id_token_hint to skip it) instead of the login form (TM-9401).
        onUnauthorized: () => {
          if (!keycloakIns.authenticated) return;
          logout({ expired: true });
        },
        // A network error here means the response never reached JS at all. Until
        // TM-9401 this was left unwired, so a whole class of failures — including
        // every CORS-blocked 401 from the gateway — left no trace whatsoever.
        //
        // Log only, deliberately. This fires for anything response-less, and that
        // includes requests the browser cancelled because the user navigated away
        // — a completely normal event. Toasting here produced "Verbindung zum
        // Server unterbrochen" out of nowhere during ordinary navigation. The
        // failure this ticket was about (an expired session) now surfaces through
        // the 401 path, so the toast bought nothing and cost a false positive.
        onNetworkError: (error) => {
          // A logout tears down in-flight requests by design; not worth logging.
          if (isLoggingOut.value) return;
          logger.warn({ msg: 'API request failed without a response', error });
        },
        // Refresh before handing the token over, so a request can never carry a
        // dead token. `updateToken(70)` resolves without a network round-trip
        // while the token is still valid, and `ensureFreshToken` collapses a burst
        // of concurrent requests onto a single refresh — necessary because the
        // realm rotates refresh tokens single-use (TM-9401).
        getAuthToken: async () => {
          if (keycloakIns.authenticated) {
            await ensureFreshToken(keycloakIns);
          }
          return keycloakIns.token;
        },
        // The tab's Active office (#1822) and caregiver entry (#1841), read per
        // request like the token.
        getOfficeId: getActiveOfficeId,
        getCaregiverId: getActiveCaregiverId,
        // A refused office or entry drops the stored choice and starts over as after login.
        onOfficeRefused: () => restartAfterOfficeRefusal(keycloakIns.subject),
      });
    }

    const app = createApp(App)
      // .use(TherameUI) // Removed: registering all components globally
      .use(pinia)
      .use(VueQueryPlugin)
      .use(autoAnimatePlugin)
      .use(GesturePlugin)
      .use(FloatingVue, {
        themes: {
          'tm-gray-900': {
            $extend: 'tooltip',
            $resetCss: true,
          },
          'tm-red-highlight': {
            $extend: 'tooltip',
            $resetCss: true,
          },
          tmWhite: {
            $extend: 'tooltip',
            $resetCss: true,
          },
          // The Muster 13 sheet's messages (TM-9664): a tooltip takes the colour of
          // the error or warning label it explains.
          'tm-red-base': {
            $extend: 'tooltip',
            $resetCss: true,
          },
          'tm-orange-base': {
            $extend: 'tooltip',
            $resetCss: true,
          },
        },
      })
      .provide('$browserName', browserName)
      .provide('$keycloak', keycloakIns) // Provide the initialized instance
      .directive('click-outside', clickOutsideDirective);

    // Vue swallows uncaught render/lifecycle/watcher errors by default (console
    // only), so a broken component reads as an empty screen with clean telemetry.
    // Installed before mount() so the very first render is already covered.
    installGlobalErrorHandlers(app);

    const authStore = useAuthStore();

    if (callSession) {
      // No offices, no „Praxis wählen“, no media cookie and no token refresh: the
      // call token names its Person, and that Person's office is the tab's.
      setAxiosCallSessionHeader(callSession.token);
      authStore.authContext = AuthContext.fromCallSession(callSession);
      await authStore.login();
    } else if (authenticated && keycloakIns.token && keycloakIns.tokenParsed) {
      setAxiosAuthHeader(keycloakIns.token);
      // Deliberately no `apiClient.setAuthToken(...)`: that installed a static
      // default header that was never refreshed, and the request interceptor
      // used to fall back to it whenever `getAuthToken()` returned falsy —
      // silently resurrecting the expired bootstrap token long after login
      // (TM-9401). The interceptor now owns the header outright.
      authStore.authContext = AuthContext.fromParsedKeycloakToken(
        keycloakIns.token,
        keycloakIns.tokenParsed as DecodedToken,
      );
      // Which context this tab acts in, before the first request that resolves
      // a Person (#1822, #1841). With several contexts and no valid stored
      // choice, nothing else loads until „Praxis wählen“ has switched in.
      authStore.accountOffices = await authQueries.listOffices();
      adoptOfficeFromUrl();
      const officeChoice = chooseOffice(keycloakIns.subject as string, authStore.accountOffices);
      authStore.officeChoicePending = officeChoice.kind === 'pick';
      if (officeChoice.kind === 'office') {
        setAxiosOfficeHeader(officeChoice.officeId);
        if (officeChoice.caregiverId) setAxiosCaregiverHeader(officeChoice.caregiverId);
      }
      if (!authStore.officeChoicePending) {
        // The media cookie has to be in place before the first exercise media
        // element renders, i.e. before mount (ADR-0038). It never throws: without
        // it, media falls back to blob downloads.
        await Promise.all([loginOrFindNoOffice(authStore), mintMediaSession()]);
      }

      startKeycloakTokenRefresh(keycloakIns);
    } else {
      authStore.logout();
      stopKeycloakTokenRefresh();
    }

    app.use(router);
    app.mount('#app');

    // Push needs a resolved Person; while „Praxis wählen“ is up, or for an
    // Account without any office, there is none. A TAN tab has no Account.
    if (!callSession && !authStore.officeChoicePending && !authStore.accountWithoutOffice)
      try {
        const pushNotificationStore = usePushNotificationStore();
        // Fire-and-forget: push notification permission check is not on the critical
        // path and must not block the initial dashboard data load.
        pushNotificationStore.checkPermission().catch((pushError) => {
          logger.warn({ msg: 'Push notification initialization failed', error: pushError });
        });

        // Re-arm push on app resume. An installed iOS PWA rarely cold-boots, and
        // WebKit can silently rotate the push subscription while the app is
        // backgrounded — so re-running checkPermission() when the app returns to the
        // foreground lets subscribePush() heal the subscription. Throttled so rapid
        // tab toggles don't spam the backend.
        let lastResumeHealAt = 0;
        const RESUME_HEAL_INTERVAL_MS = 5 * 60 * 1000;
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState !== 'visible') return;
          const now = Date.now();
          if (now - lastResumeHealAt < RESUME_HEAL_INTERVAL_MS) return;
          lastResumeHealAt = now;
          pushNotificationStore.checkPermission().catch((pushError) => {
            logger.warn({ msg: 'Push notification resume re-check failed', error: pushError });
          });
        });
      } catch (pushError) {
        logger.warn({ msg: 'Push notification initialization failed', error: pushError });
        // Continue app initialization even if push notifications fail
      }
  } catch (error) {
    logger.fatal({ msg: 'Failed to bootstrap the application', error });
    stopKeycloakTokenRefresh();

    const appElement = document.getElementById('app');
    if (appElement) {
      // Render the static (Vue-free) fallback. The Tailwind classes it uses are
      // already in the DOM via the top-level `import '@src/index.css'` above.
      appElement.innerHTML = errorFallbackHtml;

      // Fill in the error text via textContent (not innerHTML) to avoid injecting
      // markup from the error message into the page.
      const errorMessageElement = appElement.querySelector('#error-fallback-message');
      const errorDetail = formatBootstrapError(error);
      if (errorMessageElement) {
        errorMessageElement.textContent = errorDetail;
      }

      // Bind the reload button here rather than with an inline handler, so a strict
      // CSP can't block it.
      const reloadButton = appElement.querySelector('#error-fallback-reload');
      if (reloadButton) {
        reloadButton.addEventListener('click', () => window.location.reload());
      }

      // Copy the error detail to the clipboard so the user can forward it to support.
      const copyButton = appElement.querySelector('#error-fallback-copy');
      const copyLabel = appElement.querySelector('#error-fallback-copy-label');
      if (copyButton && navigator.clipboard) {
        copyButton.addEventListener('click', () => {
          navigator.clipboard
            .writeText(errorDetail)
            .then(() => {
              if (copyLabel) {
                copyLabel.textContent = 'Kopiert!';
                window.setTimeout(() => {
                  copyLabel.textContent = 'Fehler kopieren';
                }, 2000);
              }
            })
            .catch(() => {
              // Clipboard write can fail (permissions / insecure context); leave the
              // label untouched — the error is still visible for manual copying.
            });
        });
      } else if (copyButton) {
        // No Clipboard API available (e.g. insecure context): hide the affordance.
        copyButton.setAttribute('hidden', '');
      }
    }
  }
};

bootstrapApp();
